/**
 * PickerPage Component
 *
 * Main picker page showing batches ready for picking.
 * Provides batch list, print labels, print picklists functionality.
 *
 * CRITICAL: Uses `selectedLocation` from LocationContext, NOT `currentLocation`!
 *
 * Layout: Dashboard-style with MetricCards and centered container (max-w-7xl)
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import {
  Package2,
  AlertTriangle,
  Info,
  RefreshCw,
  Layers,
  PlayCircle,
  Shirt,
  Printer,
  ArrowRight,
} from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { BatchListTable } from './BatchListTable';
import { MetricCard } from './metrics';
import { usePickerBatches } from '../hooks/usePickerBatches';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function PickerPage() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  // CRITICAL: Use selectedLocation, NOT currentLocation (which doesn't exist)
  const { selectedLocation } = useLocation();

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = usePickerBatches({
    locationId: selectedLocation?.id,
  });

  // Extract batches array from query response, hiding completed/cancelled
  const allBatches = data?.batches ?? [];
  const batches = useMemo(
    () => allBatches.filter((b) => b.status !== 'COMPLETED' && b.status !== 'CANCELLED'),
    [allBatches]
  );

  // Compute metrics from active batches
  const metrics = useMemo(() => {
    const readyCount = batches.filter((b) => b.status === 'OPEN').length;
    const inProgressCount = batches.filter((b) => b.status === 'PICKING').length;
    const totalGarments = batches.reduce(
      (sum, b) => sum + b.total_pod_items + b.total_pap_items + b.total_redo_items,
      0
    );
    const needLabelsCount = batches.filter((b) => !b.labels_printed).length;

    return { readyCount, inProgressCount, totalGarments, needLabelsCount };
  }, [batches]);

  const handleBatchClick = (batchId: string) => {
    // Navigate to batch detail page for label printing
    navigate(`/production/batches/${batchId}`);
  };

  // No location selected state
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Package2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('pickerPage.title')}</h1>
            <p className="text-muted-foreground">{t('pickerPage.description')}</p>
          </div>
        </div>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t('pickerPage.selectLocation')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* ARIA Live Region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isLoading ? t('pickerPage.loadingBatches') : t('pickerPage.batchesLoaded', { count: batches.length })}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Package2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('pickerPage.title')}</h1>
            <p className="text-muted-foreground">
              {t('pickerPage.descriptionAtLocation', { location: selectedLocation.name })}
            </p>
          </div>
        </div>

        {/* Refresh button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="gap-2"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
          />
          {t('pickerPage.refresh')}
        </Button>
      </div>

      {/* Metric Cards */}
      {!isLoading && !isError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            variant="shipping"
            title={t('pickerPage.ready')}
            value={metrics.readyCount.toString()}
            subtitle={t('pickerPage.batchesWaiting')}
            icon={Layers}
          />
          <MetricCard
            variant="production"
            title={t('pickerPage.inProgress')}
            value={metrics.inProgressCount.toString()}
            subtitle={t('pickerPage.currentlyPicking')}
            icon={PlayCircle}
          />
          <MetricCard
            variant="total"
            title={t('pickerPage.totalItems')}
            value={metrics.totalGarments.toString()}
            subtitle={t('pickerPage.acrossAllBatches')}
            icon={Shirt}
          />
          <MetricCard
            variant={metrics.needLabelsCount > 0 ? 'warning' : 'gst'}
            title={t('pickerPage.needLabels')}
            value={metrics.needLabelsCount.toString()}
            subtitle={metrics.needLabelsCount > 0 ? t('pickerPage.printBeforePicking') : t('pickerPage.allPrinted')}
            icon={Printer}
          />
        </div>
      )}

      {/* Error state - standardized pattern with AlertTriangle */}
      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {error?.message || 'Failed to load batches. Please try again.'}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="ml-4"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Batch List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {!isLoading ? t('pickerPage.batchesCount', { count: batches.length }) : t('batches.title')}
            </CardTitle>
            <Link
              to="/production/batches"
              className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
            >
              {t('pickerPage.viewAllBatches')}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {/* Loading skeleton rows */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-4 py-3 border-b last:border-0">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-8 ml-auto" />
                  <Skeleton className="h-6 w-8" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-28" />
                </div>
              ))}
            </div>
          ) : (
            <BatchListTable
              batches={batches}
              onBatchClick={handleBatchClick}
              isLoading={isLoading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
